<template>
    <VJoyWrapper class="profile-gone" justify="center" direction="column">
        <MaltIllustration name="plug-unplugged--high" width="250" alt="" />
        <div class="profile-gone-text">
            {{ t('error.profile.gone') }}
        </div>
        <VJoyButton variant="main" @click="handleError">{{ ctaLabel }}</VJoyButton>
    </VJoyWrapper>
</template>

<script setup lang="ts">
    import {navigateTo} from '@typed-router';
    import type {NuxtError} from '#app';
    import {MaltIllustration} from '#components';
    import {clearError, computed, useTranslation} from '#imports';
    import {VJoyButton, VJoyWrapper} from '@maltjoy/core-vue';

    const {t} = useTranslation();

    const props = defineProps<{
        error: NuxtError;
    }>();

    type ErrorData = {
        familyOrCategoryLabel?: string;
        viewMoreUrl?: string;
    };

    const parsedErrorData = computed<ErrorData | null>(() => {
        const data = props.error?.data;

        if (!data) {
            return null;
        }

        if (typeof data === 'string') {
            try {
                return JSON.parse(data) as ErrorData;
            } catch {
                // ignored on purpose, we are already in an error rendering page
                // the original error has been reported no need to crash the error page
                // on unexpected inputs
                return null;
            }
        }

        if (typeof data === 'object') {
            return data as ErrorData;
        }

        return null;
    });

    const ctaLabel = computed<string>(() => {
        const label = parsedErrorData.value?.familyOrCategoryLabel;

        if (label) {
            return t('error.profile.gone.viewmore.cta', {0: label});
        }

        return t('error.profile.gone.viewmore.nofamily.cta');
    });

    function handleError() {
        const redirectUrl = parsedErrorData.value?.viewMoreUrl || '/';
        return navigateTo(redirectUrl, {external: true});
    }
</script>

<style lang="scss" scoped>
    .profile-gone {
        min-height: 760px;

        &-text {
            font-size: var(--joy-font-size-primary-600);
        }
    }
</style>
